package com.example.socialmediaapp.utils

import com.example.socialmediaapp.currentTimeMillis
import com.example.socialmediaapp.getLocalDateComponents
import com.example.socialmediaapp.data.models.ValidationResult

// ============================================
// TIMESTAMP FORMATTING
// ============================================

object TimeFormatter {
    fun formatTimestamp(timestamp: Long): String {
        val now = currentTimeMillis()
        val diff = now - timestamp
        return when {
            diff < 60_000L -> "Just now"
            diff < 3_600_000L -> "${diff / 60_000}m"
            diff < 86_400_000L -> formatTime(timestamp)
            diff < 604_800_000L -> formatDayOfWeek(timestamp)
            else -> formatShortDate(timestamp)
        }
    }

    fun formatTime(timestamp: Long): String {
        val c = getLocalDateComponents(timestamp)
        val minute = c.minute.toString().padStart(2, '0')
        val amPm = if (c.hour < 12) "AM" else "PM"
        val displayHour = when {
            c.hour == 0 -> 12
            c.hour > 12 -> c.hour - 12
            else -> c.hour
        }
        return "$displayHour:$minute $amPm"
    }

    fun formatFullDate(timestamp: Long): String {
        val c = getLocalDateComponents(timestamp)
        return "${monthName(c.month)} ${c.day}, ${c.year} at ${formatTime(timestamp)}"
    }

    private fun formatDayOfWeek(timestamp: Long): String {
        val c = getLocalDateComponents(timestamp)
        return when (c.dayOfWeekSun0) {
            0 -> "Sun"; 1 -> "Mon"; 2 -> "Tue"; 3 -> "Wed"
            4 -> "Thu"; 5 -> "Fri"; else -> "Sat"
        }
    }

    private fun formatShortDate(timestamp: Long): String {
        val c = getLocalDateComponents(timestamp)
        return "${monthName(c.month)} ${c.day}"
    }

    private fun monthName(month: Int): String = when (month) {
        1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"
        5 -> "May"; 6 -> "Jun"; 7 -> "Jul"; 8 -> "Aug"
        9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; else -> "Dec"
    }
}

// ============================================
// DURATION FORMATTING
// ============================================

fun formatDuration(millis: Int): String {
    val totalSeconds = millis / 1000
    val minutes = totalSeconds / 60
    val seconds = totalSeconds % 60
    return "$minutes:${seconds.toString().padStart(2, '0')}"
}

// ============================================
// ID GENERATION
// ============================================

object IdGenerator {
    fun generateId(): String {
        val chars = "abcdefghijklmnopqrstuvwxyz0123456789"
        val time = currentTimeMillis()
        val random = (0..9999999).random()
        return "${time}_${random}_${(1..8).map { chars.random() }.joinToString("")}"
    }

    fun generateMessageId() = "msg_${generateId()}"
    fun generateChatId() = "chat_${generateId()}"
}

// ============================================
// INPUT VALIDATION
// ============================================

object InputValidator {
    fun validateEmail(email: String): ValidationResult {
        val emailRegex = "^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$".toRegex()
        return when {
            email.isBlank() -> ValidationResult.Error("Email is required")
            !emailRegex.matches(email) -> ValidationResult.Error("Invalid email format")
            else -> ValidationResult.Success
        }
    }

    fun validatePassword(password: String): ValidationResult {
        return when {
            password.length < 8 -> ValidationResult.Error("Password must be at least 8 characters")
            !password.any { it.isUpperCase() } -> ValidationResult.Error("Password must contain an uppercase letter")
            !password.any { it.isLowerCase() } -> ValidationResult.Error("Password must contain a lowercase letter")
            !password.any { it.isDigit() } -> ValidationResult.Error("Password must contain a number")
            else -> ValidationResult.Success
        }
    }

    fun validateUsername(username: String): ValidationResult {
        val usernameRegex = "^[a-zA-Z0-9_]{3,20}$".toRegex()
        return when {
            username.isBlank() -> ValidationResult.Error("Username is required")
            username.length < 3 -> ValidationResult.Error("Username must be at least 3 characters")
            username.length > 20 -> ValidationResult.Error("Username must be less than 20 characters")
            !usernameRegex.matches(username) -> ValidationResult.Error("Username can only contain letters, numbers, and underscores")
            else -> ValidationResult.Success
        }
    }

    fun sanitizeInput(input: String): String = input
        .trim()
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\"", "&quot;")
        .replace("'", "&#x27;")
}

// ============================================
// MENTION PARSING
// ============================================

fun extractMentions(text: String): List<String> {
    val mentionRegex = "@([a-zA-Z0-9_]+)".toRegex()
    return mentionRegex.findAll(text)
        .map { it.groupValues[1] }
        .distinct()
        .toList()
}
